import type { PaymentDecline } from '@supply-mover-2-0/shared';

/**
 * The decline copy the API builds is written for STAFF — it says things like
 * "ask the customer for another card" and "check the customer's recent
 * transactions", which is right in the CRM and wrong here, where the reader IS
 * the customer.
 *
 * The structured decline stays the source of truth for what happened
 * (`kind`, `gatewayDetail`, `priorCharge`); only the reader-facing headline and
 * sentence are re-voiced for the storefront. Every storefront surface that
 * shows PaymentDeclineModal passes its decline through here, so a customer
 * never reads instructions addressed to someone else.
 */
export function toCustomerDecline(
	decline: PaymentDecline | null,
): PaymentDecline | null {
	if (decline == null) {
		return null;
	}

	if (decline.kind === 'duplicate') {
		return {
			...decline,
			title: 'Duplicate payment blocked',
			message:
				decline.priorCharge?.succeeded === true
					? 'This looks like a repeat of a payment you just made, and that earlier payment went through. Check your recent payments before trying again.'
					: 'This looks like a repeat of a payment you just made, which may already be processing. Check your recent payments before trying again — the block clears on its own after a few minutes.',
		};
	}

	if (decline.kind === 'declined') {
		return {
			...decline,
			title: 'Payment declined',
			message:
				'Your card issuer declined this payment. Try a different payment method, or contact your bank.',
		};
	}

	return {
		...decline,
		title: 'Payment failed',
		message:
			'The payment could not be completed. Try again, or use a different payment method.',
	};
}
